import { useMemo, useState } from 'react';
import {
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  Palette,
  Printer,
  Shapes,
  Sparkles,
} from 'lucide-react';
import { api } from '../lib/api';
import {
  bookFormats,
  comicStyles,
  characterStyles,
  trimPresets,
  bleedPageSize,
  isLargeTrim,
  minPageCount,
  type BindingMode,
  type PrintMode,
} from '../projectPresets';

type CreatedProject = Record<string, unknown>;

const wizardSteps = [
  { id: 'format', label: 'Book', icon: BookOpen },
  { id: 'comic-style', label: 'Comic style', icon: Palette },
  { id: 'character-style', label: 'Characters', icon: Shapes },
  { id: 'print', label: 'Print', icon: Printer },
  { id: 'basics', label: 'Basics', icon: Sparkles },
] as const;

export default function StartProjectWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (project: CreatedProject) => Promise<void> | void;
}) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');

  const [bookFormat, setBookFormat] = useState('graphic-novel');
  const [comicStyle, setComicStyle] = useState('modern-cinematic');
  const [characterStyle, setCharacterStyle] = useState('semi-realistic');
  const [binding, setBinding] = useState<BindingMode>('paperback');
  const [printMode, setPrintMode] = useState<PrintMode>('premium-color');
  const [trimPresetId, setTrimPresetId] = useState('7x10');
  const [customWidth, setCustomWidth] = useState(7);
  const [customHeight, setCustomHeight] = useState(10);
  const [useCustom, setUseCustom] = useState(false);
  const [bleed, setBleed] = useState(true);
  const [title, setTitle] = useState('');
  const [premise, setPremise] = useState('');
  const [targetPages, setTargetPages] = useState(48);

  const selectedTrim = useMemo(
    () => trimPresets.find(item => item.id === trimPresetId) || trimPresets[0],
    [trimPresetId]
  );

  const widthIn = useCustom ? customWidth : selectedTrim.widthIn;
  const heightIn = useCustom ? customHeight : selectedTrim.heightIn;
  const bleedSize = bleed ? bleedPageSize(widthIn, heightIn) : { widthIn, heightIn };
  const minimum = minPageCount(binding, printMode);

  const availableTrims = useMemo(
    () =>
      trimPresets.filter(item => {
        const bindingOk = binding === 'paperback' ? item.paperback : item.hardcover;
        const colorOk =
          printMode === 'black-white'
            ? item.blackWhite
            : printMode === 'standard-color'
              ? item.standardColor
              : item.premiumColor;
        return bindingOk && colorOk;
      }),
    [binding, printMode]
  );

  const applyFormat = (id: string) => {
    setBookFormat(id);
    const item = bookFormats.find(format => format.id === id);
    if (!item) return;
    setTrimPresetId(item.recommendedTrim);
    setComicStyle(item.recommendedStyle);
    setCharacterStyle(item.recommendedCharacterStyle);
    if (id === 'manga') setPrintMode('black-white');
  };

  const validateCurrent = () => {
    setErrorText('');
    if (step === 3) {
      if (useCustom) {
        if (
          binding !== 'paperback' ||
          widthIn < 4 ||
          widthIn > 8.5 ||
          heightIn < 6 ||
          heightIn > 11.69
        ) {
          setErrorText(
            'Custom KDP paperback trim must be 4–8.5 in wide and 6–11.69 in high.'
          );
          return false;
        }
      } else {
        const preset = trimPresets.find(item => item.id === trimPresetId);
        if (!preset) return false;
        if (binding === 'hardcover' && !preset.hardcover) {
          setErrorText('That trim size is not available for this hardcover setup.');
          return false;
        }
        if (printMode === 'standard-color' && !preset.standardColor) {
          setErrorText('Standard color is not available for this trim size.');
          return false;
        }
      }
      if (targetPages < minimum) setTargetPages(minimum);
    }
    if (step === 4 && !title.trim()) {
      setErrorText('Give the comic a title before creating the project.');
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateCurrent()) return;
    setStep(current => Math.min(wizardSteps.length - 1, current + 1));
  };

  const create = async () => {
    if (!validateCurrent()) return;
    setSaving(true);
    setErrorText('');
    try {
      const comicPreset = comicStyles.find(item => item.id === comicStyle)!;
      const characterPreset = characterStyles.find(
        item => item.id === characterStyle
      )!;
      const formatPreset = bookFormats.find(item => item.id === bookFormat)!;

      const payload = {
        title,
        premise,
        bookFormat,
        comicStyle,
        characterStyle,
        binding,
        printMode,
        trimPresetId: useCustom ? 'custom' : trimPresetId,
        trimWidthIn: widthIn,
        trimHeightIn: heightIn,
        bleed,
        trim: `${widthIn} x ${heightIn} in`,
        colorMode: printMode === 'standard-color' ? 'standard' : 'premium',
        targetPages: Math.max(targetPages, minimum),
        genre: formatPreset.label,
        audience: bookFormat === 'kids-comic' ? 'Children / families' : 'General',
        tone: 'Creator-defined',
        artStyle: `${comicPreset.prompt}; characters: ${characterPreset.prompt}`,
      };

      const { data } = await api.post<CreatedProject>('/api/projects', payload);
      await onCreated(data);
    } catch {
      setErrorText('The project setup could not be saved.');
      setSaving(false);
    }
  };

  const current = wizardSteps[step];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4 backdrop-blur-sm">
      <div className="max-h-[95vh] w-full max-w-6xl overflow-y-auto rounded-[30px] bg-[#fbfaf6] p-5 shadow-2xl sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <span className="eyebrow text-[#ef5b3e]">START PROJECT</span>
            <h2 className="mt-2 text-3xl font-black tracking-[-0.04em]">
              Decide the production language first.
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-black/50">
              These choices become project defaults for prompts, Character Vision,
              page proportions, print checks, cover calculations and export.
            </p>
          </div>
          <button onClick={onClose} className="iconButton" aria-label="Close">
            ×
          </button>
        </div>

        <div className="mt-6 grid grid-cols-5 gap-2">
          {wizardSteps.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => index <= step && setStep(index)}
                className={`rounded-2xl border px-2 py-3 text-center text-xs font-black ${
                  index === step
                    ? 'border-[#ef5b3e] bg-[#fff0eb] text-[#9a3927]'
                    : index < step
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-black/8 bg-white text-black/35'
                }`}
              >
                <Icon size={17} className="mx-auto mb-1.5" />
                <span className="hidden sm:inline">{item.label}</span>
                <span className="sm:hidden">{index + 1}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-7 min-h-[430px]">
          {current.id === 'format' && (
            <ChoiceGrid
              title="What are you creating?"
              subtitle="This sets useful defaults. You can still change every setting."
              items={bookFormats}
              selected={bookFormat}
              onSelect={applyFormat}
            />
          )}

          {current.id === 'comic-style' && (
            <ChoiceGrid
              title="Choose the comic's visual language."
              subtitle="This controls page-level art direction and visual rhythm."
              items={comicStyles}
              selected={comicStyle}
              onSelect={setComicStyle}
            />
          )}

          {current.id === 'character-style' && (
            <ChoiceGrid
              title="Choose how the characters are designed."
              subtitle="Character style is stored separately from the overall comic style and becomes the default for Character Vision."
              items={characterStyles}
              selected={characterStyle}
              onSelect={setCharacterStyle}
            />
          )}

          {current.id === 'print' && (
            <div>
              <div>
                <h3 className="text-2xl font-black">Set the physical book.</h3>
                <p className="mt-1 text-sm text-black/45">
                  Select a standard preset or use a valid custom paperback size.
                </p>
              </div>

              <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_340px]">
                <div className="space-y-5">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Binding">
                      <select
                        value={binding}
                        onChange={event => {
                          const nextBinding = event.target.value as BindingMode;
                          setBinding(nextBinding);
                          setUseCustom(false);
                          if (nextBinding === 'hardcover') {
                            setPrintMode(currentMode =>
                              currentMode === 'standard-color'
                                ? 'premium-color'
                                : currentMode
                            );
                            setTrimPresetId('7x10');
                          }
                        }}
                      >
                        <option value="paperback">Paperback</option>
                        <option value="hardcover">Hardcover</option>
                      </select>
                    </Field>
                    <Field label="Ink / color">
                      <select
                        value={printMode}
                        onChange={event => {
                          const nextMode = event.target.value as PrintMode;
                          setPrintMode(nextMode);
                          if (
                            nextMode === 'standard-color' &&
                            binding === 'hardcover'
                          ) {
                            setBinding('paperback');
                          }
                        }}
                      >
                        <option value="black-white">Black & white</option>
                        <option value="standard-color">Standard color</option>
                        <option value="premium-color">Premium color</option>
                      </select>
                    </Field>
                  </div>

                  {!useCustom && (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {availableTrims.map(item => (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => setTrimPresetId(item.id)}
                          className={`rounded-2xl border p-4 text-left ${
                            trimPresetId === item.id
                              ? 'border-[#ef5b3e] bg-[#fff0eb]'
                              : 'border-black/8 bg-white hover:border-black/20'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <b>{item.label}</b>
                            {trimPresetId === item.id && (
                              <Check size={17} className="text-[#ef5b3e]" />
                            )}
                          </div>
                          <div className="mt-2 text-xs text-black/40">
                            {item.category === 'kdp'
                              ? 'KDP preset'
                              : 'Industry/custom'}
                          </div>
                          {item.note && (
                            <p className="mt-2 text-xs leading-relaxed text-black/50">
                              {item.note}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {useCustom && (
                    <div className="card grid gap-4 p-5 sm:grid-cols-2">
                      <Field label="Custom width (in)">
                        <input
                          type="number"
                          min="4"
                          max="8.5"
                          step="0.001"
                          value={customWidth}
                          onChange={event =>
                            setCustomWidth(Number(event.target.value))
                          }
                        />
                      </Field>
                      <Field label="Custom height (in)">
                        <input
                          type="number"
                          min="6"
                          max="11.69"
                          step="0.001"
                          value={customHeight}
                          onChange={event =>
                            setCustomHeight(Number(event.target.value))
                          }
                        />
                      </Field>
                    </div>
                  )}

                  {binding === 'paperback' && (
                    <button
                      type="button"
                      onClick={() => setUseCustom(value => !value)}
                      className="secondary"
                    >
                      {useCustom ? 'Use standard presets' : 'Use custom trim'}
                    </button>
                  )}
                </div>

                <aside className="card p-5 h-fit">
                  <span className="eyebrow text-[#ef5b3e]">PRINT SUMMARY</span>
                  <div
                    className="mx-auto mt-5 grid place-items-center border-2 border-black/20 bg-white text-xs font-black shadow"
                    style={{
                      width: `${Math.min(190, (widthIn / heightIn) * 250)}px`,
                      height: '250px',
                    }}
                  >
                    {widthIn} × {heightIn}
                  </div>
                  <div className="mt-5 space-y-2 text-sm text-black/60">
                    <SummaryRow
                      label="Trim"
                      value={`${widthIn} × ${heightIn} in`}
                    />
                    <SummaryRow
                      label="Bleed page"
                      value={
                        bleed
                          ? `${bleedSize.widthIn} × ${bleedSize.heightIn} in`
                          : 'No bleed'
                      }
                    />
                    <SummaryRow
                      label="Class"
                      value={isLargeTrim(widthIn, heightIn) ? 'Large trim' : 'Regular trim'}
                    />
                    <SummaryRow label="Minimum pages" value={String(minimum)} />
                  </div>
                  <label className="mt-5 flex items-center gap-3 text-sm font-bold">
                    <input
                      type="checkbox"
                      checked={bleed}
                      onChange={event => setBleed(event.target.checked)}
                      className="size-4 w-auto"
                    />
                    Full-bleed artwork
                  </label>
                </aside>
              </div>
            </div>
          )}

          {current.id === 'basics' && (
            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
              <div className="card p-6">
                <h3 className="text-2xl font-black">Name the project.</h3>
                <div className="mt-5 space-y-4">
                  <Field label="Title">
                    <input
                      value={title}
                      onChange={event => setTitle(event.target.value)}
                      placeholder="The Impossible Tuesday"
                    />
                  </Field>
                  <Field label="Premise">
                    <textarea
                      rows={6}
                      value={premise}
                      onChange={event => setPremise(event.target.value)}
                      placeholder="What happens, to whom, and why should we care?"
                    />
                  </Field>
                  <Field label="Target pages">
                    <input
                      type="number"
                      min={minimum}
                      max="828"
                      value={targetPages}
                      onChange={event =>
                        setTargetPages(Number(event.target.value))
                      }
                    />
                  </Field>
                </div>
              </div>

              <aside className="card p-5 h-fit">
                <span className="eyebrow text-[#ef5b3e]">PROJECT DEFAULTS</span>
                <div className="mt-4 space-y-3 text-sm">
                  <SummaryRow
                    label="Book"
                    value={
                      bookFormats.find(item => item.id === bookFormat)?.label || ''
                    }
                  />
                  <SummaryRow
                    label="Comic"
                    value={
                      comicStyles.find(item => item.id === comicStyle)?.label || ''
                    }
                  />
                  <SummaryRow
                    label="Characters"
                    value={
                      characterStyles.find(item => item.id === characterStyle)
                        ?.label || ''
                    }
                  />
                  <SummaryRow
                    label="Print"
                    value={`${widthIn} × ${heightIn} · ${printMode.replaceAll('-', ' ')}`}
                  />
                  <SummaryRow label="Binding" value={binding} />
                </div>
              </aside>
            </div>
          )}
        </div>

        {errorText && (
          <div className="mt-4 rounded-2xl border border-[#ef5b3e]/20 bg-[#fff1ec] p-4 text-sm text-[#8d3526]">
            {errorText}
          </div>
        )}

        <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-black/8 pt-5">
          <button
            type="button"
            onClick={() =>
              step === 0 ? onClose() : setStep(currentStep => currentStep - 1)
            }
            className="secondary"
          >
            <ChevronLeft size={17} /> {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {step < wizardSteps.length - 1 ? (
            <button type="button" onClick={next} className="primary">
              Continue <ChevronRight size={17} />
            </button>
          ) : (
            <button
              type="button"
              onClick={create}
              disabled={saving}
              className="primary"
            >
              <Sparkles size={17} />
              {saving ? 'Creating…' : 'Create comic'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChoiceGrid({
  title,
  subtitle,
  items,
  selected,
  onSelect,
}: {
  title: string;
  subtitle: string;
  items: Array<{ id: string; label: string; description: string }>;
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <h3 className="text-2xl font-black">{title}</h3>
      <p className="mt-1 text-sm text-black/45">{subtitle}</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(item => (
          <button
            type="button"
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`min-h-36 rounded-2xl border p-5 text-left transition ${
              selected === item.id
                ? 'border-[#ef5b3e] bg-[#fff0eb] shadow-sm'
                : 'border-black/8 bg-white hover:-translate-y-0.5 hover:border-black/20'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <b className="text-lg">{item.label}</b>
              {selected === item.id && (
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[#ef5b3e] text-white">
                  <Check size={15} />
                </span>
              )}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-black/50">
              {item.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[.12em] text-black/45">
        {label}
      </span>
      {children}
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-black/6 pb-2">
      <span className="text-black/40">{label}</span>
      <b className="text-right capitalize">{value}</b>
    </div>
  );
}
